import ExcelJS from 'exceljs';
import type { Contact, CrmData, CrmUser, Project, ProjectUpdate } from '../types/crm';
import { clientById, contactLabel } from '../utils/crmHelpers';
import { CRM_TIMEZONE, formatSingaporeDate, formatSingaporeDateTime, parseCrmDate } from '../utils/dateTime';
import {
  authorDisplayName,
  isSystemProjectUpdate,
  updatesForProject,
} from '../utils/projectHelpers';

const ENQUIRY_HEADERS = [
  'S/N',
  'DATE',
  'SERVE BY',
  'CUSTOMER NAME',
  'CONTACT PERSON',
  'TITLE',
  'UPDATES',
  'STATUS',
  'COMPLETION DATE',
] as const;

const ENQUIRY_COLUMN_WIDTHS = [6, 12, 14, 28, 13, 33, 46, 18, 20];

export interface ProjectExportContext {
  data: CrmData;
  ownerLabel: (ownerRef?: string) => string;
}

export function exportStatusLabel(status?: string): string {
  if (status === 'Completed') return 'Closed';
  if (status === 'On Hold') return 'ON HOLD';
  return 'PROCESSING';
}

/** DD/MM/YYYY — enquiry log and exports. */
export function formatEnquiryLogDate(iso?: string | null): string {
  const formatted = formatSingaporeDate(iso);
  return formatted === '—' ? '' : formatted;
}

function authorFirstName(users: CrmUser[], update: ProjectUpdate): string {
  const full = authorDisplayName(update, users).trim();
  if (!full || full === 'System') return 'System';
  const token = full.split(/\s+/)[0] ?? full;
  return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
}

export function buildProjectUpdatesText(
  updates: ProjectUpdate[],
  users: CrmUser[],
  projectId: string,
): string {
  const list = updatesForProject(updates, projectId).filter(
    (u) => !isSystemProjectUpdate(u.content),
  );
  const sorted = [...list].sort((a, b) =>
    (b.created_at ?? '').localeCompare(a.created_at ?? ''),
  );

  return sorted
    .map((u) => {
      const when = formatEnquiryLogDate(u.created_at);
      const who = authorFirstName(users, u);
      let body = u.content?.trim() ?? '';
      if (u.attachment_name) {
        body = body
          ? `${body}\n[Attachment: ${u.attachment_name}]`
          : `[Attachment: ${u.attachment_name}]`;
      }
      if (!body) body = '—';
      return `${when} ${who}:\n${body}`;
    })
    .join('\n\n');
}

export function contactPersonExport(contact?: Contact): string {
  if (!contact) return '';
  const lines = [contactLabel(contact)];
  if (contact.phone?.trim()) lines.push(contact.phone.trim());
  if (contact.email?.trim() && !contact.phone?.trim()) lines.push(contact.email.trim());
  return lines.join('\n');
}

export interface EnquiryExportRow {
  sn: number;
  date: Date | null;
  serveBy: string;
  customerName: string;
  contactPerson: string;
  title: string;
  updates: string;
  status: string;
  completionDate: Date | null;
}

export function buildEnquiryRow(
  project: Project,
  sn: number,
  ctx: ProjectExportContext,
): EnquiryExportRow {
  const client = clientById(ctx.data.clients, project.client_id);
  const contact = ctx.data.contacts.find((c) => c.id === project.contact_id);
  const completedRaw =
    project.completed_date ?? project.completed_at ?? null;
  const completionDate =
    project.status === 'Completed' ? parseCrmDate(completedRaw) : null;

  return {
    sn,
    date: parseCrmDate(project.created_at),
    serveBy: ctx.ownerLabel(project.owner_id).toUpperCase(),
    customerName: client?.company_name?.trim() ?? '',
    contactPerson: contactPersonExport(contact),
    title: project.title?.trim() ?? '',
    updates: buildProjectUpdatesText(ctx.data.updates, ctx.data.users, project.id),
    status: exportStatusLabel(project.status),
    completionDate,
  };
}

async function styleEnquirySheet(sheet: ExcelJS.Worksheet) {
  ENQUIRY_COLUMN_WIDTHS.forEach((w, i) => {
    sheet.getColumn(i + 1).width = w;
  });

  const header = sheet.getRow(2);
  header.height = 30;
  header.font = { name: 'Calibri', size: 11, bold: true };
  header.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
  header.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFFFFF00' },
    };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  sheet.views = [{ state: 'frozen', ySplit: 2 }];
}

function appendEnquiryDataRows(sheet: ExcelJS.Worksheet, rows: EnquiryExportRow[]) {
  for (const row of rows) {
    const excelRow = sheet.addRow([
      row.sn,
      row.date ?? '',
      row.serveBy,
      row.customerName,
      row.contactPerson,
      row.title,
      row.updates,
      row.status,
      row.completionDate ?? '',
    ]);
    excelRow.alignment = { vertical: 'top', wrapText: true };

    const dateCell = excelRow.getCell(2);
    if (row.date) {
      dateCell.value = row.date;
      dateCell.numFmt = 'dd/mm/yyyy';
    }

    const completionCell = excelRow.getCell(9);
    if (row.completionDate) {
      completionCell.value = row.completionDate;
      completionCell.numFmt = 'dd/mm/yyyy';
    }

    excelRow.eachCell((cell) => {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
      cell.font = { name: 'Calibri', size: 11 };
    });
  }
}

export async function buildCustomersEnquiryWorkbook(
  projects: Project[],
  ctx: ProjectExportContext,
): Promise<ExcelJS.Workbook> {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'BTH CRM';
  const sheet = wb.addWorksheet('New enqueries');
  sheet.addRow([]);
  sheet.addRow([...ENQUIRY_HEADERS]);

  const rows = projects.map((p, i) => buildEnquiryRow(p, i + 1, ctx));
  appendEnquiryDataRows(sheet, rows);
  await styleEnquirySheet(sheet);
  return wb;
}

export async function downloadWorkbook(workbook: ExcelJS.Workbook, filename: string) {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function exportCustomersEnquiryExcel(projects: Project[], ctx: ProjectExportContext) {
  const wb = await buildCustomersEnquiryWorkbook(projects, ctx);
  const stamp = new Intl.DateTimeFormat('en-CA', {
    timeZone: CRM_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  await downloadWorkbook(wb, `CUSTOMERS ENQUIRY ${stamp}.xlsx`);
}

/** Teams folder style: `07. project updates by Sales - Jul'26.xlsx` */
export function monthlyTeamsEnquiryFileName(
  date = new Date(),
  portal: 'crm' | 'it' = 'crm',
): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: CRM_TIMEZONE,
    month: '2-digit',
    year: '2-digit',
  }).formatToParts(date);
  const mm = parts.find((p) => p.type === 'month')?.value ?? '01';
  const yy = parts.find((p) => p.type === 'year')?.value ?? '00';
  const mon = new Intl.DateTimeFormat('en-US', {
    timeZone: CRM_TIMEZONE,
    month: 'short',
  }).format(date);
  const label = portal === 'it' ? 'IT' : 'Sales';
  return `${mm}. project updates by ${label} - ${mon}'${yy}.xlsx`;
}

export function currentSingaporeYearMonth(date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: CRM_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
  }).format(date);
}

export function isIncompleteProject(status?: string | null): boolean {
  return (status ?? 'Ongoing').trim() !== 'Completed';
}

export async function workbookToBuffer(workbook: ExcelJS.Workbook): Promise<ArrayBuffer> {
  const buffer = await workbook.xlsx.writeBuffer();
  if (buffer instanceof ArrayBuffer) return buffer;
  const view = buffer as ArrayBufferView;
  return view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength) as ArrayBuffer;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatReportDateTime(iso?: string | null): string {
  return formatSingaporeDateTime(iso);
}

export function renderProjectReportHtml(project: Project, ctx: ProjectExportContext): string {
  const client = clientById(ctx.data.clients, project.client_id);
  const contact = ctx.data.contacts.find((c) => c.id === project.contact_id);
  const updates = updatesForProject(ctx.data.updates, project.id).filter(
    (u) => !isSystemProjectUpdate(u.content),
  );
  const sorted = [...updates].sort((a, b) =>
    (b.created_at ?? '').localeCompare(a.created_at ?? ''),
  );
  const completedRaw = project.completed_date ?? project.completed_at;
  const generatedAt = formatReportDateTime(new Date().toISOString());
  const statusClass =
    project.status === 'Completed'
      ? 'completed'
      : project.status === 'On Hold'
        ? 'hold'
        : 'ongoing';

  const timelineHtml =
    sorted.length === 0
      ? '<p class="empty">No updates recorded yet.</p>'
      : sorted
          .map((u) => {
            const who = authorDisplayName(u, ctx.data.users);
            const when = formatReportDateTime(u.created_at);
            const body = escapeHtml(u.content?.trim() || '—').replace(/\n/g, '<br/>');
            const attach = u.attachment_name
              ? `<div class="log__attach"><span class="log__attach-label">Attachment</span> ${escapeHtml(u.attachment_name)}</div>`
              : '';
            return `<article class="log"><div class="log__content"><header class="log__head"><div class="log__who">${escapeHtml(who)}</div><time class="log__time">${escapeHtml(when)}</time></header><div class="log__body">${body}</div>${attach}</div></article>`;
          })
          .join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Project Report — ${escapeHtml(project.reference_no ?? project.title)}</title>
  <style>
    @page { size: A4; margin: 0; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      padding: 44px 40px 48px;
      font-family: "Segoe UI", "Helvetica Neue", Calibri, Arial, sans-serif;
      font-size: 10pt;
      color: #1e293b;
      line-height: 1.5;
      background: #ffffff;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .sheet {
      width: 100%;
      max-width: 100%;
      background: #ffffff;
      border-radius: 12px;
      border: 1px solid #d8e0ea;
      overflow: hidden;
    }
    .brand {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      gap: 24px;
      padding: 28px 32px 26px;
      background: linear-gradient(128deg, #9b2222 0%, #6e1515 48%, #4a0e0e 100%);
      color: #fff;
    }
    .brand__title { margin: 0; font-size: 20pt; font-weight: 700; letter-spacing: 0.02em; line-height: 1.15; }
    .brand__sub { margin: 6px 0 0; font-size: 9.5pt; font-weight: 500; opacity: 0.88; letter-spacing: 0.12em; text-transform: uppercase; }
    .brand__meta {
      text-align: right;
      font-size: 8.5pt;
      line-height: 1.55;
      opacity: 0.9;
      padding-bottom: 2px;
    }
    .brand__meta strong { display: block; font-weight: 600; margin-bottom: 2px; }
    .main { padding: 28px 32px 32px; }
    .hero { margin-bottom: 28px; }
    .hero__top { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; margin-bottom: 20px; }
    .hero__title { margin: 0; font-size: 18pt; font-weight: 700; color: #0f172a; line-height: 1.25; letter-spacing: -0.02em; }
    .hero__ref { margin: 8px 0 0; font-size: 9.5pt; color: #64748b; }
    .badge {
      flex-shrink: 0;
      display: inline-block;
      padding: 6px 14px;
      border-radius: 999px;
      font-size: 7.5pt;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.1em;
    }
    .badge.ongoing { background: #dbeafe; color: #1d4ed8; }
    .badge.completed { background: #dcfce7; color: #15803d; }
    .badge.hold { background: #ffedd5; color: #c2410c; }
    .grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      margin: 0;
      padding: 0;
      list-style: none;
    }
    .grid__item {
      margin: 0;
      padding: 14px 16px;
      background: #f8fafc;
      border: 1px solid #e8edf3;
      border-radius: 10px;
    }
    .grid dt { margin: 0 0 6px; font-size: 7pt; text-transform: uppercase; letter-spacing: 0.1em; color: #94a3b8; font-weight: 700; }
    .grid dd { margin: 0; font-size: 9.5pt; font-weight: 600; color: #1e293b; line-height: 1.4; }
    .section__head {
      display: flex;
      align-items: center;
      gap: 12px;
      margin: 0 0 18px;
      padding-bottom: 10px;
      border-bottom: 2px solid #f1e0e0;
    }
    .section__head h3 {
      margin: 0;
      font-size: 8.5pt;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.14em;
      color: #9b2222;
    }
    .section__count {
      font-size: 8pt;
      color: #94a3b8;
      font-weight: 600;
    }
    .timeline { display: flex; flex-direction: column; gap: 12px; }
    .log {
      margin: 0;
      padding: 0 0 0 16px;
      border-left: 3px solid #e2c8c8;
      page-break-inside: avoid;
    }
    .log:first-child { border-left-color: #9b2222; }
    .log__content {
      padding: 14px 18px;
      background: #fafbfc;
      border: 1px solid #e8edf3;
      border-radius: 10px;
    }
    .log__head { display: flex; justify-content: space-between; align-items: baseline; gap: 16px; margin-bottom: 8px; }
    .log__who { font-size: 9.5pt; font-weight: 700; color: #0f172a; }
    .log__time { font-size: 8.5pt; color: #64748b; white-space: nowrap; }
    .log__body { font-size: 9.5pt; color: #334155; line-height: 1.55; }
    .log__attach {
      margin-top: 10px;
      padding-top: 10px;
      border-top: 1px dashed #dde3ea;
      font-size: 8.5pt;
      color: #64748b;
    }
    .log__attach-label {
      font-weight: 700;
      text-transform: uppercase;
      font-size: 7pt;
      letter-spacing: 0.08em;
      color: #94a3b8;
      margin-right: 6px;
    }
    .empty {
      margin: 0;
      padding: 24px;
      text-align: center;
      color: #94a3b8;
      font-style: italic;
      background: #f8fafc;
      border-radius: 10px;
      border: 1px dashed #dde3ea;
    }
    .footer {
      margin: 0;
      padding: 18px 32px 22px;
      border-top: 1px solid #e8edf3;
      font-size: 7.5pt;
      color: #94a3b8;
      text-align: center;
      letter-spacing: 0.04em;
      background: #fafbfc;
    }
  </style>
</head>
<body>
  <div class="sheet">
    <header class="brand">
      <div>
        <h1 class="brand__title">BTH CRM</h1>
        <p class="brand__sub">Project activity report</p>
      </div>
      <div class="brand__meta">
        <strong>Generated</strong>
        ${escapeHtml(generatedAt)}<br/>
        Singapore (UTC+8)
      </div>
    </header>
    <div class="main">
      <section class="hero">
        <div class="hero__top">
          <div>
            <h2 class="hero__title">${escapeHtml(project.title)}</h2>
            <p class="hero__ref">Reference · ${escapeHtml(project.reference_no ?? '—')}</p>
          </div>
          <span class="badge ${statusClass}">${escapeHtml(project.status ?? 'Ongoing')}</span>
        </div>
        <dl class="grid">
          <div class="grid__item"><dt>Account</dt><dd>${escapeHtml(client?.company_name ?? '—')}</dd></div>
          <div class="grid__item"><dt>Account code</dt><dd>${escapeHtml(client?.code ?? '—')}</dd></div>
          <div class="grid__item"><dt>Key contact</dt><dd>${escapeHtml(contact ? contactPersonExport(contact).replace(/\n/g, ' · ') : '—')}</dd></div>
          <div class="grid__item"><dt>Project owner</dt><dd>${escapeHtml(ctx.ownerLabel(project.owner_id))}</dd></div>
          <div class="grid__item"><dt>Created</dt><dd>${escapeHtml(formatReportDateTime(project.created_at))}</dd></div>
          <div class="grid__item"><dt>Completed</dt><dd>${escapeHtml(project.status === 'Completed' ? formatReportDateTime(completedRaw) : '—')}</dd></div>
        </dl>
      </section>
      <section class="section">
        <div class="section__head">
          <h3>Update timeline</h3>
          <span class="section__count">${sorted.length} update${sorted.length === 1 ? '' : 's'}</span>
        </div>
        <div class="timeline">
          ${timelineHtml}
        </div>
      </section>
    </div>
    <footer class="footer">Confidential — for internal use · BTH CRM Web</footer>
  </div>
</body>
</html>`;
}

function safeReportFilename(project: Project): string {
  const base = (project.reference_no || project.title || 'project').trim();
  return base.replace(/[^\w.-]+/g, '_').slice(0, 80) || 'project';
}

/** Renders A4 project report and downloads as PDF (no pop-up). */
export async function downloadProjectReportPdf(
  project: Project,
  ctx: ProjectExportContext,
): Promise<void> {
  const html = renderProjectReportHtml(project, ctx);
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText =
    'position:fixed;left:-10000px;top:0;width:794px;min-height:1200px;border:0;visibility:hidden;';
  document.body.appendChild(iframe);

  try {
    const doc = iframe.contentDocument ?? iframe.contentWindow?.document;
    if (!doc) throw new Error('Could not prepare report document.');

    doc.open();
    doc.write(html);
    doc.close();

    await new Promise<void>((resolve) => {
      if (doc.readyState === 'complete') {
        resolve();
        return;
      }
      iframe.onload = () => resolve();
    });
    await new Promise((r) => setTimeout(r, 250));

    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
      import('html2canvas'),
      import('jspdf'),
    ]);

    const captureRoot = doc.body;
    if (!captureRoot.querySelector('.sheet')) throw new Error('Report layout failed to render.');

    const canvas = await html2canvas(captureRoot, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    });

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const imgData = canvas.toDataURL('image/png');

    let offsetY = 0;
    let heightLeft = imgHeight;

    pdf.addImage(imgData, 'PNG', 0, offsetY, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      offsetY = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, offsetY, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(`${safeReportFilename(project)}_Report.pdf`);
  } finally {
    document.body.removeChild(iframe);
  }
}
